import React, { useState } from "react";
import { toast } from "react-toastify";

import EcoConfirmDialog from "../../../components/eco-ui/EcoConfirmDialog";
import {
  createCostCenterGroupApi,
  deleteCostCenterGroupApi,
  DpCostCenterGroup,
  updateCostCenterGroupApi,
} from "../api/settingsApi";

type Props = {
  token: string;
  /** Árvore com os inativos (tela de manutenção). */
  groups: DpCostCenterGroup[];
  /** Recarrega grupos e centros depois de salvar. */
  onChanged: () => Promise<void> | void;
};

type Form = { name: string; parentId: string; isActive: boolean };
const emptyForm: Form = { name: "", parentId: "", isActive: true };

/**
 * Grupos e subgrupos de centro de custo (DRE por centro de custo, 17/10/2026).
 * Dois níveis: grupo → subgrupo. O centro de custo escolhe o grupo no próprio
 * cadastro. Grupo com subgrupo ou com centro não se exclui: inativa-se.
 */
export default function CostCenterGroupsSettings({ token, groups, onChanged }: Props) {
  const [modal, setModal] = useState<{ id?: string } | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<DpCostCenterGroup | null>(null);

  const open = (group?: DpCostCenterGroup, parentId?: string) => {
    setError(null);
    setForm(
      group
        ? {
            name: group.name,
            parentId: group.parentId ?? "",
            isActive: group.isActive,
          }
        : { ...emptyForm, parentId: parentId ?? "" }
    );
    setModal({ id: group?.id });
  };

  const editing = modal?.id ? groups.flatMap((g) => [g, ...g.children]).find((g) => g.id === modal.id) : null;
  // Grupo com subgrupos não vira subgrupo (só dois níveis).
  const canHaveParent = !editing || editing.children.length === 0;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.name.trim().length < 2) {
      setError("Informe o nome.");
      return;
    }
    const payload = {
      name: form.name.trim(),
      parentId: form.parentId || null,
      isActive: form.isActive,
    };
    setSaving(true);
    setError(null);
    try {
      if (modal?.id) await updateCostCenterGroupApi(token, modal.id, payload);
      else await createCostCenterGroupApi(token, payload);
      toast.success("Grupo salvo.");
      setModal(null);
      await onChanged();
    } catch (err: any) {
      setError(err?.message || "Erro ao salvar o grupo.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!removing) return;
    setSaving(true);
    try {
      await deleteCostCenterGroupApi(token, removing.id);
      toast.success("Grupo excluído.");
      setRemoving(null);
      await onChanged();
    } catch (err: any) {
      toast.error(err?.message || "Erro ao excluir o grupo.");
      setRemoving(null);
    } finally {
      setSaving(false);
    }
  };

  const row = (g: DpCostCenterGroup, isChild: boolean) => (
    <tr key={g.id} className={isChild ? "dpccGroups__child" : ""}>
      <td data-label="Grupo">
        <span className={isChild ? "dpccGroups__subname" : "dpccGroups__name"}>
          <span className="dpccGroups__code">{g.code}</span> {g.name}
        </span>
      </td>
      <td data-label="Centros de custo">
        {g.costCenters.length ? g.costCenters.map((c) => `${c.code} ${c.name}`).join(", ") : "—"}
      </td>
      <td data-label="Situação">
        <span className={`dpBadge ${g.isActive ? "dpBadge--green" : "dpBadge--gray"}`}>{g.isActive ? "Ativo" : "Inativo"}</span>
      </td>
      <td data-label="">
        <div className="dpTable__actions">
          {!isChild ? (
            <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => open(undefined, g.id)}>
              Novo subgrupo
            </button>
          ) : null}
          <button className="dpBtn dpBtn--secondary dpBtn--small" onClick={() => open(g)}>
            Editar
          </button>
          <button className="dpBtn dpBtn--danger dpBtn--small" onClick={() => setRemoving(g)}>
            Excluir
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <section className="dpccGroups" aria-labelledby="dpcc-groups-title">
      <div className="dpccGroups__head">
        <h3 id="dpcc-groups-title" className="dpccGroups__title">
          Grupos e subgrupos
        </h3>
        <button className="dpBtn dpBtn--secondary" onClick={() => open()}>
          Novo grupo
        </button>
      </div>
      <p className="dpFormHint">
        Organizam os centros de custo na DRE do Financeiro (ex.: 1 Administrativo; 2 Operacional → 2.1 Rio de Janeiro).
        Dois níveis: grupo e subgrupo. O código e a ordem são gerados pelo sistema, na ordem de cadastro: grupos 1, 2,
        3…; subgrupos do grupo 1 são 1.1, 1.2… O grupo de cada centro se escolhe no cadastro do centro, abaixo.
      </p>

      <div className="dpTableWrapper">
        <table className="dpTable dpccGroups__table">
          <thead>
            <tr>
              <th>Grupo</th>
              <th>Centros de custo</th>
              <th>Situação</th>
              <th style={{ width: 280 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {groups.length === 0 ? (
              <tr>
                <td colSpan={4} className="dpEmpty">
                  Nenhum grupo. Sem grupo, a DRE mostra os centros de custo em "Sem grupo".
                </td>
              </tr>
            ) : (
              groups.flatMap((g) => [row(g, false), ...g.children.map((c) => row(c, true))])
            )}
          </tbody>
        </table>
      </div>

      {modal ? (
        <div className="dpModalOverlay">
          <div className="dpModal" role="dialog" aria-modal="true" aria-label="Grupo de centro de custo">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {modal.id ? "Editar grupo" : form.parentId ? "Novo subgrupo" : "Novo grupo"}
              </h2>
              <button className="dpModal__close" onClick={() => setModal(null)} disabled={saving} aria-label="Fechar">
                &times;
              </button>
            </header>
            <form onSubmit={save}>
              <div className="dpModal__body">
                {error ? <p className="dpNotice dpNotice--warning">{error}</p> : null}
                {editing ? (
                  <p className="dpFormHint">
                    Código <strong>{editing.code}</strong> (gerado pelo sistema).
                  </p>
                ) : (
                  <p className="dpFormHint">O código é gerado ao salvar: o próximo número {form.parentId ? "do grupo escolhido" : "de grupo"}.</p>
                )}
                <div className="dpFormGroup">
                  <label htmlFor="ccgName">Nome *</label>
                  <input
                    id="ccgName"
                    className="dpInput"
                    maxLength={120}
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    disabled={saving}
                  />
                </div>
                <div className="dpFormGrid">
                  <div className="dpFormGroup">
                    <label htmlFor="ccgParent">Subgrupo de</label>
                    <select
                      id="ccgParent"
                      className="dpSelect"
                      value={form.parentId}
                      onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value }))}
                      disabled={saving || !canHaveParent}
                    >
                      <option value="">Nenhum (é um grupo)</option>
                      {groups
                        .filter((g) => g.id !== modal.id)
                        .map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.code} — {g.name}
                          </option>
                        ))}
                    </select>
                    {!canHaveParent ? (
                      <span className="dpFormHint">Este grupo tem subgrupos: só há dois níveis.</span>
                    ) : editing && form.parentId !== (editing.parentId ?? "") ? (
                      <span className="dpFormHint">Ao trocar o grupo-pai, o código muda para a numeração do pai novo.</span>
                    ) : null}
                  </div>
                </div>
                <div className="dpFormGroup">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.isActive}
                      onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                      disabled={saving}
                    />{" "}
                    Ativo
                  </label>
                </div>
              </div>
              <footer className="dpModal__footer">
                <button type="button" className="dpBtn dpBtn--secondary" onClick={() => setModal(null)} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
                  {saving ? "Salvando..." : "Salvar"}
                </button>
              </footer>
            </form>
          </div>
        </div>
      ) : null}

      <EcoConfirmDialog
        open={removing !== null}
        title="Excluir grupo?"
        message={
          removing
            ? `"${removing.name}" será excluído. Grupo com subgrupo ou com centro de custo não se exclui: mova os centros antes, ou inative o grupo.`
            : ""
        }
        confirmLabel="Excluir"
        tone="danger"
        busy={saving}
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </section>
  );
}
